import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BarChart3, Target, Pencil, TrendingUp, TrendingDown, Minus, Download, AlertTriangle, AlertCircle, Lightbulb, Sparkles, CheckCircle2, ArrowRight, Wallet, Loader2, Star, Clock, Users } from "lucide-react";
import { api, apiError, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import GoalRing from "@/components/reports/GoalRing";
import ProjectionChart from "@/components/reports/ProjectionChart";
import GoalsModal from "@/components/reports/GoalsModal";
import ExpressHistory from "@/components/reports/ExpressHistory";
import { periodLabel, periodOptions, METRIC_UNIT } from "@/components/reports/meta";

const VIEW_LABEL = { overview: "Visão geral", creator: "Criador", freelancer: "Freelancer" };
const GRAN = [{ v: "month", l: "Mês" }, { v: "quarter", l: "Trimestre" }, { v: "year", l: "Ano" }];
const IMPROVE = {
  alta: { label: "Prioridade alta", icon: AlertTriangle, cls: "border-red-500/30 bg-red-500/[0.06] text-red-300", dot: "bg-red-400" },
  atencao: { label: "Atenção", icon: AlertCircle, cls: "border-amber-500/30 bg-amber-500/[0.06] text-amber-300", dot: "bg-amber-400" },
  oportunidade: { label: "Oportunidade", icon: Lightbulb, cls: "border-sky-500/30 bg-sky-500/[0.06] text-sky-300", dot: "bg-sky-400" },
};

const fmtVal = (v, unit) => {
  if (v == null) return "—";
  if (unit === "R$") return money(v);
  if (unit === "%") return `${v}%`;
  if (unit === "h") return `${v}h`;
  if (unit === "★") return v ? `${v}` : "—";
  return `${v}`;
};

const KpiCard = ({ k }) => {
  const unit = k.unit || "";
  const hasDelta = k.delta != null && k.prev != null;
  const improved = hasDelta && (k.lower_better ? k.delta < 0 : k.delta > 0);
  const worse = hasDelta && k.delta !== 0 && !improved;
  const DeltaIcon = !hasDelta || k.delta === 0 ? Minus : (k.delta > 0 ? TrendingUp : TrendingDown);
  return (
    <div data-testid={`kpi-${k.key}`} className="rounded-2xl border border-white/10 bg-[#121217] p-4 min-w-0">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground truncate flex items-center gap-1">
        {unit === "★" && <Star className="h-3 w-3 text-accent" />}{k.label}
      </div>
      <div className="mt-1.5 text-2xl font-bold tracking-tight text-foreground flex items-baseline gap-0.5">
        {fmtVal(k.value, unit)}{unit === "★" && k.value ? <span className="text-sm text-accent">/5</span> : null}
      </div>
      {hasDelta && (
        <div data-testid={`kpi-delta-${k.key}`} className={`mt-1 text-[11px] font-medium flex items-center gap-0.5 ${improved ? "text-emerald-400" : worse ? "text-red-400" : "text-muted-foreground"}`}>
          <DeltaIcon className="h-3 w-3" />{k.delta > 0 ? "+" : ""}{k.delta_pct}% <span className="text-muted-foreground font-normal">vs anterior</span>
        </div>
      )}
    </div>
  );
};

export default function Reports() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [gran, setGran] = useState("month");
  const [period, setPeriod] = useState(periodOptions("month")[0]);
  const [view, setView] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [goalsOpen, setGoalsOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const exportPdf = async () => {
    setExporting(true);
    try {
      const r = await api.get("/reports/metrics/export", { params: { period, ...(view ? { view } : {}) }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `relatorio-${period}-${view}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Não foi possível exportar o PDF."); }
    finally { setExporting(false); }
  };

  const load = useCallback(() => {
    setLoading(true);
    const params = { period }; if (view) params.view = view;
    api.get("/reports/metrics", { params })
      .then((r) => { setData(r.data); if (!view) setView(r.data.view); })
      .catch((e) => toast.error(apiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [period, view]);
  useEffect(() => { load(); }, [load]);

  const changeGran = (g) => { setGran(g); setPeriod(periodOptions(g)[0]); };

  const toggleAction = async (a) => {
    const next = !a.done;
    setData((d) => ({ ...d, actions: d.actions.map((x) => x.id === a.id ? { ...x, done: next } : x) }));
    try { await api.post("/reports/actions/toggle", { action_id: a.id, period: data.period.period, done: next }); }
    catch { toast.error("Não foi possível atualizar a ação."); load(); }
  };

  if (loading && !data) return <div className="py-20 flex justify-center" data-testid="reports-loading"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>;
  if (!data) return <div className="text-muted-foreground">Sem dados.</div>;

  const p = data.primary, prog = p?.progress;
  const doneCount = (data.actions || []).filter((a) => a.done).length;
  const allowTabs = (data.allowed_views || []).length > 1;

  return (
    <div className="max-w-5xl mx-auto space-y-5 pb-10" data-testid="reports-page">
      {/* 1. Cabeçalho + período */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-head text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2.5"><BarChart3 className="h-7 w-7 text-[#FF4D00]" />Relatórios & Metas</h1>
          <p className="text-muted-foreground text-sm mt-1">Seu desempenho, suas metas e o que fazer para chegar lá.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-xl border border-white/10 overflow-hidden" data-testid="gran-toggle">
            {GRAN.map((g) => (
              <button key={g.v} data-testid={`gran-${g.v}`} onClick={() => changeGran(g.v)}
                className={`h-9 px-3 text-xs font-medium transition-colors ${gran === g.v ? "bg-[#FF4D00] text-white" : "bg-transparent text-muted-foreground hover:text-foreground"}`}>{g.l}</button>
            ))}
          </div>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger data-testid="period-select" className="h-9 w-[160px] bg-[#121217] border-white/10 rounded-xl"><SelectValue>{periodLabel(period)}</SelectValue></SelectTrigger>
            <SelectContent className="bg-[#121217] border-white/10">{periodOptions(gran).map((o) => <SelectItem key={o} value={o}>{periodLabel(o)}</SelectItem>)}</SelectContent>
          </Select>
          <Button data-testid="export-pdf-btn" onClick={exportPdf} disabled={exporting} variant="outline" size="icon" className="h-9 w-9 rounded-xl bg-transparent border-white/10 shrink-0" aria-label="Exportar relatório em PDF">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {/* 2. Seletor de visão */}
      {allowTabs && (
        <div className="grid grid-cols-3 gap-1.5 p-1 rounded-2xl border border-white/10 bg-[#121217]" data-testid="view-tabs">
          {data.allowed_views.map((v) => (
            <button key={v} data-testid={`view-tab-${v}`} onClick={() => setView(v)}
              className={`h-10 rounded-xl text-sm font-semibold transition-colors ${view === v ? "bg-[#FF4D00] text-white shadow-[0_0_18px_rgba(255,79,0,0.3)]" : "text-muted-foreground hover:text-foreground"}`}>{VIEW_LABEL[v]}</button>
          ))}
        </div>
      )}

      {/* 3. Meta principal */}
      <section data-testid="main-goal-card" className="rounded-2xl border border-white/10 bg-[#121217] p-5 sm:p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <span className="text-[11px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5"><Target className="h-3.5 w-3.5 text-accent" />Meta principal · {periodLabel(period)}</span>
          <Button data-testid="edit-goals-btn" onClick={() => setGoalsOpen(true)} variant="outline" size="sm" className="rounded-xl bg-transparent border-white/10 gap-1.5 h-8"><Pencil className="h-3.5 w-3.5" />Editar metas</Button>
        </div>
        {prog ? (
          <div className="flex flex-col sm:flex-row items-center gap-5 sm:gap-8">
            <GoalRing pct={prog.pct} onTrack={prog.on_track}>
              <span className="text-3xl font-extrabold text-foreground">{prog.pct}%</span>
              <span className="text-[11px] text-muted-foreground">atingido</span>
            </GoalRing>
            <div className="flex-1 min-w-0 text-center sm:text-left">
              <h2 className="font-head text-xl font-bold text-foreground">{p.label}: {prog.target}{METRIC_UNIT[p.metric] === "%" ? "%" : ""}</h2>
              <p className="text-sm text-muted-foreground mt-1">{fmtVal(prog.current, METRIC_UNIT[p.metric])} de {fmtVal(prog.target, METRIC_UNIT[p.metric])} · faltam {fmtVal(prog.remaining, METRIC_UNIT[p.metric])}</p>
              <div className={`mt-3 inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg border ${prog.on_track ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" : "border-amber-500/30 bg-amber-500/10 text-amber-300"}`}>
                {prog.on_track ? <CheckCircle2 className="h-4 w-4" /> : <TrendingUp className="h-4 w-4" />}
                {prog.on_track ? "No ritmo certo" : "Abaixo do ritmo necessário"}
              </div>
              {prog.projection != null && prog.metric_type !== "rate" && (
                <p className="text-sm text-muted-foreground mt-3">Mantendo o ritmo atual, a projeção é terminar com <span className="text-foreground font-semibold">{prog.projection}</span>.{prog.per_week_needed ? <> Para atingir a meta, você precisa de aproximadamente <span className="text-foreground font-semibold">{prog.per_week_needed}</span> por semana.</> : null}</p>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center py-6">
            <p className="text-sm text-muted-foreground">Você ainda não definiu uma meta principal.</p>
            <Button data-testid="set-goal-empty-btn" onClick={() => setGoalsOpen(true)} className="mt-3 bg-[#FF4D00] hover:bg-[#E64500] rounded-xl gap-1.5"><Target className="h-4 w-4" />Definir meta</Button>
          </div>
        )}
      </section>

      {/* 4. KPIs */}
      <section>
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">Como você está</div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="kpi-grid">
          {(data.kpis || []).map((k) => <KpiCard key={k.key} k={k} />)}
        </div>
      </section>

      {/* 5. Projeção */}
      {prog && (
        <section data-testid="projection-card" className="rounded-2xl border border-white/10 bg-[#121217] p-5">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-1.5"><TrendingUp className="h-3.5 w-3.5 text-accent" />Projeção da meta</div>
          <ProjectionChart progress={prog} period={data.period} />
        </section>
      )}

      {/* 6. O que precisa melhorar */}
      {(data.improve || []).length > 0 && (
        <section data-testid="improve-section">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">O que precisa da sua atenção</div>
          <div className="grid sm:grid-cols-3 gap-3">
            {data.improve.map((it, i) => {
              const cfg = IMPROVE[it.level] || IMPROVE.atencao; const Icon = cfg.icon;
              return (
                <div key={i} data-testid={`improve-${it.level}-${i}`} className={`rounded-2xl border p-4 ${cfg.cls}`}>
                  <div className="flex items-center gap-2 text-xs font-semibold"><Icon className="h-4 w-4" /><span className={`h-1.5 w-1.5 rounded-full ${cfg.dot}`} />{cfg.label}</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">{it.title}</div>
                  <p className="mt-1 text-xs text-muted-foreground leading-relaxed">{it.text}</p>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* 7. Top 3 ações */}
      {(data.actions || []).length > 0 && (
        <section data-testid="actions-section" className="rounded-2xl border border-white/10 bg-[#121217] p-5">
          <div className="flex items-center justify-between mb-1">
            <div className="text-sm font-semibold text-foreground flex items-center gap-1.5"><Sparkles className="h-4 w-4 text-accent" />Suas 3 ações prioritárias</div>
            <span data-testid="actions-progress" className="text-xs text-muted-foreground">Plano desta semana — {doneCount}/{data.actions.length} concluído</span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-white/8 overflow-hidden mb-4"><div className="h-full bg-gradient-to-r from-[#FFD24D] to-[#FFAA00]" style={{ width: `${(doneCount / data.actions.length) * 100}%`, transition: "width .5s ease" }} /></div>
          <div className="space-y-2">
            {data.actions.map((a) => (
              <div key={a.id} data-testid={`action-${a.id}`} className="flex items-center gap-3 rounded-xl border border-white/10 bg-[#09090B] p-3">
                <button data-testid={`action-check-${a.id}`} onClick={() => toggleAction(a)} aria-pressed={a.done} aria-label={`Marcar "${a.title}" como concluída`}
                  className={`h-6 w-6 shrink-0 rounded-md border flex items-center justify-center transition-colors ${a.done ? "bg-emerald-500 border-emerald-500" : "border-white/20 hover:border-white/40"}`}>
                  {a.done && <CheckCircle2 className="h-4 w-4 text-white" />}
                </button>
                <span className={`flex-1 text-sm ${a.done ? "line-through text-muted-foreground" : "text-foreground"}`}>{a.title}</span>
                <Button data-testid={`action-cta-${a.id}`} onClick={() => navigate(a.cta_route)} size="sm" variant="outline" className="rounded-lg bg-transparent border-primary/30 text-primary gap-1 shrink-0 h-8"><span className="hidden sm:inline">{a.cta_label}</span><ArrowRight className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* 8. Indicadores complementares */}
      {(data.complementary || []).length > 0 && (
        <Accordion type="single" collapsible className="rounded-2xl border border-white/10 bg-[#121217] px-5" data-testid="complementary-accordion">
          <AccordionItem value="comp" className="border-b-0">
            <AccordionTrigger data-testid="complementary-trigger" className="text-sm text-foreground hover:no-underline">Indicadores complementares</AccordionTrigger>
            <AccordionContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pb-2">
                {data.complementary.map((k) => (
                  <div key={k.key} data-testid={`comp-${k.key}`} className="rounded-xl border border-white/10 bg-[#09090B] p-3">
                    <div className="text-[11px] text-muted-foreground truncate">{k.label}</div>
                    <div className="mt-1 text-lg font-bold text-foreground">{fmtVal(k.value, k.unit)}</div>
                  </div>
                ))}
              </div>
              {(data.top_instruments?.length > 0 || data.top_types?.length > 0) && (
                <div className="grid sm:grid-cols-2 gap-3 pt-1">
                  {data.top_instruments?.length > 0 && <div className="rounded-xl border border-white/10 bg-[#09090B] p-3"><div className="text-[11px] text-muted-foreground mb-1.5 flex items-center gap-1"><Users className="h-3 w-3" />Instrumentos mais contratados</div>{data.top_instruments.map((t) => <div key={t.name} className="flex justify-between text-sm"><span className="text-foreground">{t.name}</span><span className="text-muted-foreground">{t.count}</span></div>)}</div>}
                  {data.top_types?.length > 0 && <div className="rounded-xl border border-white/10 bg-[#09090B] p-3"><div className="text-[11px] text-muted-foreground mb-1.5 flex items-center gap-1"><Clock className="h-3 w-3" />Tipos de evento mais contratados</div>{data.top_types.map((t) => <div key={t.name} className="flex justify-between text-sm"><span className="text-foreground">{t.name}</span><span className="text-muted-foreground">{t.count}</span></div>)}</div>}
                </div>
              )}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}

      {/* 9. Insights */}
      {(data.insights || []).length > 0 && (
        <section data-testid="insights-card" className="rounded-2xl border border-accent/20 bg-accent/[0.04] p-5">
          <div className="text-sm font-semibold text-accent flex items-center gap-1.5 mb-2"><Sparkles className="h-4 w-4" />Insights do Mí Chamou</div>
          <ul className="space-y-1.5">
            {data.insights.map((t, i) => <li key={i} data-testid={`insight-${i}`} className="text-sm text-muted-foreground flex items-start gap-2"><span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-accent shrink-0" />{t}</li>)}
          </ul>
        </section>
      )}

      <ExpressHistory />

      {/* 10. Rodapé → Financeiro */}
      <div data-testid="finance-footer" className="rounded-2xl border border-white/10 bg-[#121217] p-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5">
          <Wallet className="h-5 w-5 text-accent" />
          <div><div className="text-sm font-semibold text-foreground">Valores e movimentações</div><div className="text-xs text-muted-foreground">Saldo, recebimentos, Pix e repasses continuam no Financeiro.</div></div>
        </div>
        <Button data-testid="go-finance-btn" onClick={() => navigate("/pagamentos")} variant="outline" className="rounded-xl bg-transparent border-white/10 gap-1.5">Ver no Financeiro<ArrowRight className="h-4 w-4" /></Button>
      </div>

      <GoalsModal open={goalsOpen} onOpenChange={setGoalsOpen} onSaved={load} period={period} />
    </div>
  );
}
